import React from 'react';
import { Modal, Button, Form } from 'react-bulma-components';
import { TextField, MenuItem, InputAdornment } from '@material-ui/core';
import { toast } from 'react-toastify';
import AccountsStore from 'stores/AccountsOverview/AccountsStore';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import moment from 'moment';
import { FaDownload, FaUpload, FaTrash } from 'react-icons/fa';
import { observable, toJS } from 'mobx';

const localUserData = localStorage.getItem('userData') || '';
const userData = localUserData ? JSON.parse(localUserData) : {};
const userGroups = userData?.user?.usergroups ? userData.user.usergroups.map(x => x.group) : [];
const adminAccess = userData?.user?.isAdmin || false;
const canEdit = adminAccess ||  userGroups.includes('KeyAccountManager') ? true : false;

class AccountEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      account: props.account || {
        number: '',
        bc_bill_name: '',
        channel: '',
        listing_type: '',
        managedBy: '',
        discount: '',
        bc_tax_country: '',
        bc_warehouse_location: '',
        pos_closed: '',
        pos_potential: '',
        forecast_revenue: '',
        customer_since: '',
        switch_central_delivery: '',
        bc_document_sending_profile: '',
        bc_email: '',
        email: '',
        bc_gln: '',
        bc_vat: '',
        bc_payment_terms: '',
        closed_listings_count: '',
        potential_listings_count: '',
        wkz: '',
        AccountsOverviewFiles: [],
      },
      originalAccount: { ...props.account },
      showDeleteConfirmation: false,
      filesToUpload: [],
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.account !== this.props.account) {
      this.setState({
        account: this.props.account || this.state.account,
        originalAccount: { ...this.props.account },
      });
    }
  }

  handleChange = (e) => {
    e.persist(); // Prevent the synthetic event from being reused
    const { name, value } = e.target;
    this.setState((prevState) => ({
      account: {
        ...prevState.account,
        [name]: value,
      },
    }));
  };

  handleChangeAssociation = (e) => {
    e.persist(); // Prevent the synthetic event from being reused
    const { name, value } = e.target;
    const list =
      name === 'AccountsOverviewGroup' ? AccountsStore.list.groups : AccountsStore.list.managers;
    const item = list.find((x) => x.name === value);

    this.setState((prevState) => ({
      account: {
        ...prevState.account,
        [`${name}Id`]: item.id, //check whether automatically pulled in update,
        [name]: item,
      },
    }));
  };

  handleSave = async () => {
    const { account, filesToUpload } = this.state;
    let uploadRes;
    try {
      if(!canEdit) {
        throw "You dont have permission to edit accounts!"
      }
      await AccountsStore.updateAccount(account);
      if (filesToUpload.length > 0) {
        uploadRes = await AccountsStore.uploadFiles(account.id, filesToUpload);

        if (!uploadRes.success) {
          toast.warning('Account submitted successfully, but file upload failed');
          throw (
            uploadRes.error?.message ||
            uploadRes.error ||
            uploadRes.message ||
            JSON.stringify(uploadRes) ||
            'Error uploading file'
          );
        }
      }
      toast.success('Account saved successfully');
      if (this.props.onSave) {
        this.props.onSave();
      }
      this.setState({
        originalAccount: { ...account },
        account: {
          ...account,
          AccountsOverviewFiles: [
            ...(account.AccountsOverviewFiles || []),
            ...(uploadRes?.data || []),
          ],
        },
        filesToUpload: [],
      });
    } catch (error) {
      console.error('Failed to save account:', error);
      toast.error(`Failed to save account: ${error}`);
    }
  };

  deleteFile = async (fileToDelete) => {
    const { account } = this.state;
    const { AccountsOverviewFiles = [] } = account;

    if (!window.confirm('Do you really want to delete this file?')) return;

    try {
      const res = await AccountsStore.deleteFile(fileToDelete);

      if (!res.success) {
        toast.error(
          res.error?.message ||
            res.error ||
            res.message ||
            JSON.stringify(res) ||
            'Error deleting file'
        );
        return;
      }

      const updatedFiles = AccountsOverviewFiles.filter((file) => file.id !== fileToDelete.id);

      this.setState({
        account: {
          ...account,
          AccountsOverviewFiles: updatedFiles,
        },
      });

      toast.success('File successfully deleted!');
    } catch (error) {
      console.error('Deletion failed:', error);
      toast.error('Unexpected error deleting file');
    }
  };

  handleDelete = async () => {
    const { account } = this.state;
    try {
      await AccountsStore.deleteItem(account, 'accounts');
      toast.success('Account deleted successfully');
      if (this.props.onSave) {
        this.props.onSave();
      }
      this.setState({ showDeleteConfirmation: false });
      this.props.onClose();
    } catch (error) {
      console.error('Failed to delete account:', error);
      toast.error('Failed to delete account');
    }
  };

  toggleDeleteConfirmation = () => {
    this.setState((prevState) => ({
      showDeleteConfirmation: !prevState.showDeleteConfirmation,
    }));
  };

  handleClose = () => {
    this.setState({ account: { ...this.state.originalAccount } });
    this.props.onClose();
  };

  downloadFile = async (file) => {
    try {
      const data = await AccountsStore.downloadFile(file);
      if (!data) throw 'Missing download data';
      const downloadFile = new File([data], file.name);
      const blob = new Blob([downloadFile], { type: file.mime_type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.setAttribute('download', file.name);
      link.setAttribute('href', url);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.error?.message ||
          error?.error ||
          error?.message ||
          JSON.stringify(error) ||
          'Error downloading file'
      );
    }
  };

  onFileChange = async (e) => {
    const { filesToUpload } = this.state;
    const newFiles = e.target?.files || [];
    const currentFiles = filesToUpload.filter(
      (x) => ![...newFiles].map((x) => x.name).includes(x.name)
    );

    this.setState({
      filesToUpload: [...currentFiles, ...newFiles],
    });
    e.target.value = null;
  };

  render() {
    const { isOpen } = this.props;
    const { account, showDeleteConfirmation, filesToUpload } = this.state;

    const readOnlyStyle = {
      backgroundColor: '#f5f5f5',
      cursor: 'not-allowed',
    };

    if (!account) {
      return null;
    }

    return (
      <>
        <Modal show={isOpen} onClose={this.handleClose}>
          <Modal.Card style={{ width: '65%' }}>
            <Modal.Card.Header>
              <Modal.Card.Title>
                Edit Account {account.number} - {account.bc_bill_name}
              </Modal.Card.Title>
            </Modal.Card.Header>
            <Modal.Card.Body>
              <div style={{ width: '95%', margin: '0 auto' }}>
                <Form.Field>
                  <Form.Label>Group</Form.Label>
                  <Form.Control>
                    <TextField
                      select
                      name="AccountsOverviewGroup"
                      value={account.AccountsOverviewGroup?.name || ''}
                      onChange={this.handleChangeAssociation}
                      fullwidth
                    >
                      {AccountsStore.list.groups?.map((group) => (
                        <MenuItem key={group.id} value={group.name}>
                          {group.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Channel</Form.Label>
                  <Form.Control>
                    <TextField
                      select
                      name="channel"
                      value={account.channel || ''}
                      onChange={this.handleChange}
                      fullwidth
                    >
                      {AccountsStore?.channels.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Type of Listing</Form.Label>
                  <Form.Control>
                    <TextField
                      select
                      name="listing_type"
                      value={account.listing_type || ''}
                      onChange={this.handleChange}
                      fullwidth
                    >
                      {AccountsStore?.listingTypes.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Managed By</Form.Label>
                  <Form.Control>
                    <TextField
                      select
                      name="AccountsOverviewManager"
                      value={account.AccountsOverviewManager?.name || ''}
                      onChange={this.handleChangeAssociation}
                      fullwidth
                    >
                      {AccountsStore.list.managers?.map((manager) => (
                        <MenuItem key={manager.id} value={manager.name}>
                          {manager.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Discount Rate</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="discount"
                      value={account.discount || ''}
                      onChange={this.handleChange}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>WKZ composition</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="wkz_composition"
                      value={account.wkz_composition || ''}
                      onChange={this.handleChange}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Tax Country</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="bc_tax_country"
                      value={account.bc_tax_country || ''}
                      readOnly
                      style={readOnlyStyle}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>SKUs Listed</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="closed_listings_count"
                      value={account.closed_listings_count || ''}
                      readOnly
                      style={readOnlyStyle}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Customer Since</Form.Label>
                  <Form.Control>
                    <Form.Input
                      type="date"
                      name="customer_since"
                      value={
                        account.customer_since
                          ? moment(account.customer_since).format('YYYY-MM-DD')
                          : ''
                      }
                      onChange={this.handleChange}
                      placeholder="DD.MM.YYYY"
                      fullwidth
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Start Zentralregulierung</Form.Label>
                  <Form.Control>
                    <Form.Input
                      type="date"
                      name="switch_central_delivery"
                      value={
                        account.switch_central_delivery
                          ? moment(account.switch_central_delivery).format('YYYY-MM-DD')
                          : ''
                      }
                      onChange={this.handleChange}
                      placeholder="DD.MM.YYYY"
                      fullwidth
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Belegsendeprofil</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="bc_document_sending_profile"
                      value={account.bc_document_sending_profile || ''}
                      readOnly
                      style={readOnlyStyle}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Mailadresse BC</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="bc_email"
                      value={account.bc_email || ''}
                      readOnly
                      style={readOnlyStyle}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Mailadresse Kontakt</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="email"
                      value={account.email || ''}
                      onChange={this.handleChange}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Invoice GLN</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="bc_gln"
                      value={account.bc_gln || ''}
                      readOnly
                      style={readOnlyStyle}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>VAT ID</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="bc_vat"
                      value={account.bc_vat || ''}
                      readOnly
                      style={readOnlyStyle}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Payment Terms</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="bc_payment_terms"
                      value={account.bc_payment_terms || ''}
                      readOnly
                      style={readOnlyStyle}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Warehouse Location</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="bc_warehouse_location"
                      value={account.bc_warehouse_location || ''}
                      readOnly
                      style={readOnlyStyle}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>POS Closed</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="pos_closed"
                      value={account.pos_closed || ''}
                      readOnly
                      style={readOnlyStyle}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>POS Potential</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="pos_potential"
                      value={account.pos_potential || ''}
                      onChange={this.handleChange}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Forecast Revenue</Form.Label>
                  <Form.Control>
                    <Form.Input
                      name="forecast_revenue"
                      value={account.forecast_revenue || ''}
                      onChange={this.handleChange}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field>
                  <Form.Label>Files</Form.Label>
                  <Form.Control>
                    {account.AccountsOverviewFiles.map((file, i) => {
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <p>{file.name}</p>
                          <FaDownload style={styles.icon} onClick={() => this.downloadFile(file)} />
                          <FaTrash style={styles.icon} onClick={() => this.deleteFile(file)} />
                        </div>
                      );
                    })}
                    {filesToUpload.length > 0
                      ? filesToUpload.map((file, i) => {
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <p>{file.name}</p>
                              <FaTrash
                                style={styles.icon}
                                onClick={() =>
                                  this.setState({
                                    filesToUpload: filesToUpload.filter((_, index) => index !== i)
                                  })
                                }
                              />
                            </div>
                          );
                        })
                      : ''}
                    <input
                      id="file-upload"
                      type="file"
                      disabled={false}
                      style={{ marginTop: '20px' }}
                      multiple
                      onChange={(e) => this.onFileChange(e)}
                    />
                  </Form.Control>
                </Form.Field>
                <div style={{ textAlign: 'right', marginTop: '25px' }}>
                  <Button color="danger" onClick={this.toggleDeleteConfirmation}>
                    Delete Account
                  </Button>
                </div>
              </div>
            </Modal.Card.Body>
            <Modal.Card.Footer style={{ justifyContent: 'flex-end' }}>
              <Button color="success" onClick={this.handleSave} style={{ width: '150px' }}>
                Save
              </Button>
              <Button color="grey" onClick={this.handleClose} style={{ width: '150px' }}>
                Close
              </Button>
            </Modal.Card.Footer>
          </Modal.Card>
        </Modal>

        <Modal show={showDeleteConfirmation} onClose={this.toggleDeleteConfirmation}>
          <Modal.Card>
            <Modal.Card.Header>
              <Modal.Card.Title>Confirm Deletion</Modal.Card.Title>
            </Modal.Card.Header>
            <Modal.Card.Body>
              <p>Are you sure you want to delete this account?</p>
            </Modal.Card.Body>
            <Modal.Card.Footer style={{ justifyContent: 'flex-end' }}>
              <Button color="danger" onClick={this.handleDelete}>
                Yes, Delete
              </Button>
              <Button onClick={this.toggleDeleteConfirmation}>Cancel</Button>
            </Modal.Card.Footer>
          </Modal.Card>
        </Modal>
      </>
    );
  }
}

const styles = {
  icon: {
    cursor: 'pointer',
  },
};

export default AccountEditor;
